import { useDndMonitor, useDroppable } from "@dnd-kit/core";
import { faCube } from "@fortawesome/pro-regular-svg-icons";
import { Box, ButtonBase, SxProps, Theme, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { styled, useTheme } from "@mui/material/styles";
import { Accept } from "react-dropzone";

import { UploadableModality } from "@/constants/dropzone";
import { useShowSnackbar } from "@/hooks";
import { useDropzoneFileUpload } from "@/hooks/useDropzoneFileUpload";

import { ContainerLoader } from "../ContainerLoader/ContainerLoader";
import { DisabledArea } from "../DisabledArea/DisabledArea";
import { FilepickerFile } from "../FilePicker/types";
import { Icon } from "../Icon/Icon";
import { AudioIcon, VideoIcon } from "../icons";
import { UploadCloudIcon } from "../icons/UploadCloudIcon";
import { UploadReferenceIcon } from "../icons/UploadReferenceIcon";

interface FileSelectAreaProps {
  autoFocus?: boolean;
  onClick: () => void;
  onUpload: (files: FilepickerFile[]) => void;
  loading?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  label?: string;
  sx?: SxProps<Theme>;
  accept?: Accept;
  maxFiles?: number;
  testId?: string;
  modality: UploadableModality;
  onDropped?: (item: any) => void;
  isDroppableEnabled?: boolean;
  droppableAccepts?: string[];
  droppableId?: string;
  bottomLabel?: string;
}

const classes = {
  iconContainer: "FileSelectArea-iconContainer",
};

const Container = styled("div")<{ disabled?: boolean }>(
  ({ theme, disabled }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(3),
    width: "100%",
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(6, 0),

    ...(disabled && {
      opacity: 0.5,
      cursor: "not-allowed",
    }),
  }),
);

const IconContainer = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "isDragActive",
})<{ isDragActive: boolean }>(({ theme, isDragActive, disabled }) => ({
  color: theme.palette.secondary[2],
  borderRadius: 6,
  display: "flex",
  justifyContent: "center",
  width: 64,
  height: 64,
  opacity: disabled ? 0.5 : 1,
  transition: "all 0.2s ease-in-out",
  border: `dashed 1px ${isDragActive ? theme.palette.primary.light : theme.palette.secondary[4]}`,
  "&:hover": {
    borderColor: theme.palette.secondary[2],
    color: theme.palette.secondary[1],
  },
}));

export const FileSelectArea = (props: FileSelectAreaProps) => {
  const {
    autoFocus,
    onClick,
    onUpload,
    label = "Select file(s)",
    loading: externalLoading,
    disabled,
    disabledTooltip,
    accept,
    maxFiles,
    sx,
    testId,
    modality,
    isDroppableEnabled = false,
    droppableAccepts,
    onDropped,
    droppableId,
    bottomLabel,
  } = props;
  const { getRootProps, getInputProps, isDragActive, isUploading } =
    useDropzoneFileUpload({
      modality,
      accept,
      maxFiles,
      disabled,
      noClick: true,
      noKeyboard: true,
      onUploadSuccess: onUpload,
    });

  const theme = useTheme();

  const { showError } = useShowSnackbar();

  const { setNodeRef, isOver } = useDroppable({
    id: droppableId ?? "file-select-area",
    disabled: !isDroppableEnabled || disabled,
    data: {
      accepts: droppableAccepts,
    },
  });

  useDndMonitor({
    onDragEnd(event) {
      const { over, active } = event;

      if (over?.id === droppableId && active?.data?.current) {
        const { item, type } = active.data.current;

        if (item && type && over?.data?.current?.accepts?.includes(type)) {
          onDropped?.(item);
        } else {
          showError(
            "The file you dropped isn’t in a supported format. Please try again.",
          );
        }
      }
    },
  });

  const isLoading = externalLoading || isUploading;

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", height: 120 }}>
        <ContainerLoader />
      </Box>
    );
  }

  return (
    <DisabledArea disabled={disabled} title={disabledTooltip}>
      <Box ref={setNodeRef} sx={{ width: "100%" }}>
        <Container
          {...getRootProps()}
          sx={{
            ...sx,
            gap: bottomLabel ? 0 : theme.spacing(3),
            padding: bottomLabel ? theme.spacing(2, 0) : theme.spacing(6, 0),
          }}
        >
          <input {...getInputProps()} />
          <IconContainer
            isDragActive={isDragActive || isOver}
            className={classes.iconContainer}
            onClick={onClick}
            disabled={disabled}
            data-testid="file-select-trigger"
            sx={{
              height: bottomLabel ? 84 : 64,
              width: bottomLabel ? 84 : 64,
            }}
          >
            {modality === "IMAGE" && (
              <UploadReferenceIcon sx={{ fontSize: 46 }} />
            )}
            {modality === "VIDEO" && <VideoIcon sx={{ fontSize: 46 }} />}
            {modality === "AUDIO" && <AudioIcon sx={{ fontSize: 46 }} />}
            {modality === "THREE_D" && (
              <Icon icon={faCube} style={{ fontSize: 32 }} />
            )}
          </IconContainer>
          {!bottomLabel && (
            <Button
              autoFocus={autoFocus}
              rounded
              disabled={disabled}
              data-testid={testId}
              color="secondary"
              onClick={onClick}
              variant="contained"
              startIcon={
                <UploadCloudIcon sx={{ height: "16px", width: "16px" }} />
              }
            >
              {label}
            </Button>
          )}
          {bottomLabel && (
            <Typography
              color={
                disabled
                  ? theme.palette.secondary[3]
                  : theme.palette.secondary[2]
              }
              sx={{ mt: 2 }}
            >
              {bottomLabel}
            </Typography>
          )}
        </Container>
      </Box>
    </DisabledArea>
  );
};
